import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import { MAXIMUM_TOPICS } from "../../components/Topics/constants";
import { useTopicsData } from "../../hooks/topics/useTopicsData";
import useKeywordsGroups from "../../hooks/useKeywordsGroups";

import type { ProviderSchema, TopicsDataContextSchema } from "./types";

const initialState: TopicsDataContextSchema = {
    createdTopics: [],
    isSuggestion: false,
    isMaxCreated: false,
    data: [],
    isDisabled: false,
    sorting: null,
    filter: null,
    flags: null,
    isLoading: false,
    createdTopicsLength: 0,
    keywordsGroups: { sharedKWList: [], ownKWList: [] },
    setSorting: noop,
    setFilter: noop,
};

export const TopicsDataContext = createContext(initialState);

export const TopicsDataContextProvider = ({
    children,
    duration,
    isTopicsCreated,
    createdTopicsLength,
    isActivationLoading,
}: PropsWithChildren<ProviderSchema>) => {
    const {
        params: { key: activationId, timeGranularity: granularity },
    } = useCurrentStateAndParams();

    const {
        isDisabled,
        createdTopics,
        data,
        flags,
        isLoading = isActivationLoading,
        sorting,
        filter,
        setSorting,
        setFilter,
    } = useTopicsData({
        activationId,
        duration,
        granularity,
        isTopicsCreated,
        createdTopicsLength,
    });

    const isSuggestion = !isTopicsCreated;
    const isMaxCreated = createdTopicsLength >= MAXIMUM_TOPICS;

    const { keywordsGroups } = useKeywordsGroups(createdTopics, isLoading);

    return (
        <TopicsDataContext.Provider
            value={{
                isSuggestion,
                isMaxCreated,
                createdTopics,
                data,
                isDisabled,
                sorting,
                filter,
                flags,
                isLoading,
                createdTopicsLength,
                keywordsGroups,
                setSorting,
                setFilter,
            }}
        >
            {children}
        </TopicsDataContext.Provider>
    );
};

export const useTopicsDataContext = () => {
    const context = useContext(TopicsDataContext);

    if (context === undefined) {
        throw new Error("useTopicsDataContext must be used within a TopicsDataContextProvider");
    }

    return context;
};
